import React from 'react';
import {
  Form,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
  useActionData,
  useOutlet,
  useLocation
} from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

import * as Headless from '@headlessui/react';
import {
  DocumentChartBarIcon,
  DocumentIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { PageTitle } from '@oysterjs/uiv2/page';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import { Select } from '@oysterjs/uiv2/select';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/uiv2/fieldset';

import {
  CreateHtmlDocumentTemplateMutation,
  HtmlDocumentTemplateFieldsFragment,
  HtmlDocumentTemplateInput,
  UpdateHtmlDocumentTemplateMutation
} from '../../api/graphql';
import { Await, TransitionDiv } from '@oysterjs/uiv2/async';
import { Button } from '@oysterjs/uiv2/button';
import { Textarea } from '@oysterjs/uiv2/textarea';
import { FileInput } from '@oysterjs/uiv2/file';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { getStaticsBaseUrl } from '@oysterjs/corev2/config';
import { MountableSlideout } from '@oysterjs/uiv2/slideout';

export const DocumentTemplatesPage = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | { data: Promise<{ htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Styles" description="Create predefined document styles for Cara to generate">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between gap-4 flex-col xs:flex-row">
            <form className="w-full max-w-md">
              <Fieldset>
                <FieldGroup>
                  <div className="flex gap-4">
                    <Field className="w-full max-w-md">
                      <Label className="sr-only" data-slot="none">
                        Filter table
                      </Label>
                      <InputGroup>
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <Input
                          type="search"
                          name="search"
                          placeholder="Start typing to filter..."
                        />
                      </InputGroup>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </form>
            <Button color="sky" href="/app/styles/new" className="flex-none">
              Create Style
            </Button>
          </div>
        </div>

        <React.Suspense fallback={<DocumentTemplatesPageLoader />}>
          <Await resolve={loader?.data}>
            {({ htmlDocumentTemplates }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white shadow">
                {!htmlDocumentTemplates?.length && (
                  <div className="text-center py-12">
                    <DocumentChartBarIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">No styles found</h3>
                    <p className="mt-1 text-sm text-neutral-500">Create a style to get started.</p>
                  </div>
                )}
                {!!htmlDocumentTemplates?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Title</TableHeader>
                        <TableHeader className="py-4">Created By</TableHeader>
                        <TableHeader className="py-4">Last Updated</TableHeader>
                        <TableHeader className="py-4">
                          <div className="sr-only">Actions</div>
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {htmlDocumentTemplates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell>{template.title}</TableCell>
                          <TableCell>{template.createdBy.name}</TableCell>
                          <TableCell>
                            {new Date(template.updatedAt).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-row gap-4">
                              <Headless.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app/styles/${template.id}`);
                                }}
                                className="font-medium text-primary-600 hover:text-primary-900"
                              >
                                Edit
                              </Headless.Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </PageTitle>
  );
};

export const DocumentTemplatesPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading styles...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const DocumentTemplateCreatePage = () => {
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateHtmlDocumentTemplateMutation>;
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const submitForm = (form: HtmlDocumentTemplateInput) => {
    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('baseStyleId', form.baseStyleId || '');
    formData.append('prefixFileId', form.prefixFileId || '');
    formData.append('suffixFileId', form.suffixFileId || '');
    form.uploadPrefixFile && formData.append('uploadPrefixFile', form.uploadPrefixFile);
    form.uploadSuffixFile && formData.append('uploadSuffixFile', form.uploadSuffixFile);

    submit(formData, { method: 'POST', encType: 'multipart/form-data' });
  };

  return (
    <DocumentTemplatesCreateOrEditPage
      editMode={false}
      initialForm={{
        title: '',
        description: '',
        baseStyleId: 'builtin:classic-professional'
      }}
      onSubmit={submitForm}
      validationErrors={validationErrors}
    />
  );
};

export const DocumentTemplateEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ htmlDocumentTemplate?: HtmlDocumentTemplateFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.htmlDocumentTemplate) {
            return null;
          }

          const submit = useSubmit();
          const action = useActionData() as ActionResult<UpdateHtmlDocumentTemplateMutation>;
          const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

          React.useEffect(() => {
            if (action?.validationErrors) {
              setValidationErrors(action.validationErrors);
            }
          }, [action?.validationErrors]);

          const submitForm = (form: HtmlDocumentTemplateInput) => {
            const formData = new FormData();
            formData.append('title', form.title);
            formData.append('description', form.description);
            formData.append('baseStyleId', form.baseStyleId || '');
            formData.append('prefixFileId', form.prefixFileId || '');
            formData.append('suffixFileId', form.suffixFileId || '');
            form.uploadPrefixFile && formData.append('uploadPrefixFile', form.uploadPrefixFile);
            form.uploadSuffixFile && formData.append('uploadSuffixFile', form.uploadSuffixFile);

            submit(formData, { method: 'PUT', encType: 'multipart/form-data' });
          };

          return (
            <DocumentTemplatesCreateOrEditPage
              editMode
              initialTemplate={data.htmlDocumentTemplate}
              initialForm={{
                title: data.htmlDocumentTemplate.title || '',
                description: data.htmlDocumentTemplate.description || '',
                baseStyleId: data.htmlDocumentTemplate.baseStyleId || null,
                prefixFileId: data.htmlDocumentTemplate.prefixFiles?.[0]?.id || null,
                suffixFileId: data.htmlDocumentTemplate.suffixFiles?.[0]?.id || null
              }}
              onSubmit={submitForm}
              validationErrors={validationErrors}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const DocumentTemplatesCreateOrEditPage = (props: {
  editMode?: boolean;
  initialForm: HtmlDocumentTemplateInput;
  initialTemplate?: HtmlDocumentTemplateFieldsFragment;
  onSubmit: (form: HtmlDocumentTemplateInput) => void;
  validationErrors: ValidationError[];
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const action = useActionData() as ActionResult<CreateHtmlDocumentTemplateMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<HtmlDocumentTemplateInput>(props.initialForm);
  const [prefixFiles, _setPrefixFiles] = React.useState<File[]>([]);
  const [suffixFiles, _setSuffixFiles] = React.useState<File[]>([]);
  const { updateForm: _updateForm, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );
  const [renderingPreview, setRenderingPreview] = React.useState(false);
  const [renderPreview, setRenderPreview] = React.useState(!!props.editMode);
  const [renderPreviewVersion, setRenderPreviewVersion] = React.useState(0);
  const [resetPrefixFiles, _setResetPrefixFiles] = React.useState(false);
  const [resetSuffixFiles, _setResetSuffixFiles] = React.useState(false);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.onSubmit({
      ...form,
      // TODO: handle reseting the prefix/suffix files without uploading new files
      prefixFileId:
        prefixFiles.length === 0 && !resetPrefixFiles ? form.prefixFileId || null : null,
      suffixFileId:
        suffixFiles.length === 0 && !resetSuffixFiles ? form.suffixFileId || null : null,
      uploadPrefixFile: prefixFiles.length > 0 ? prefixFiles[0] : null,
      uploadSuffixFile: suffixFiles.length > 0 ? suffixFiles[0] : null
    });
  };

  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.formAction === navigation.location?.pathname &&
      !action?.validationErrors?.length
    ) {
      regeneratePreview();
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors
  ]);

  React.useEffect(() => {
    if (props.editMode) {
      regeneratePreview();
    }
  }, []);

  const regeneratePreview = () => {
    setRenderPreview(false);
    setRenderingPreview(true);
    setTimeout(() => {
      _setResetPrefixFiles(false);
      _setResetSuffixFiles(false);
      setRenderingPreview(false);
      setRenderPreview(true);
      setRenderPreviewVersion((prev) => prev + 1);
    }, 1500);
  };

  const setPrefixFiles = (files: File[]) => {
    _setPrefixFiles(files);
    setRenderPreview(false);
  };

  const setSuffixFiles = (files: File[]) => {
    _setSuffixFiles(files);
    setRenderPreview(false);
  };

  const setResetPrefixFiles = (value: boolean) => {
    _setResetPrefixFiles(value);
    setRenderPreview(false);
  };

  const setResetSuffixFiles = (value: boolean) => {
    _setResetSuffixFiles(value);
    setRenderPreview(false);
  };

  const updateForm = (
    fieldParts: string[],
    updateFn: (prev: HtmlDocumentTemplateInput) => HtmlDocumentTemplateInput
  ) => {
    _updateForm(fieldParts, updateFn);
    setRenderPreview(false);
  };

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/styles');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout
      onClose={onClose}
      title={props.editMode ? 'Edit style' : 'Create a new style'}
      size="6xl"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
        <Form onSubmit={handleSubmit} className="col-span-1">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Title</Label>
                <Description>A brief, descriptive title for the style template.</Description>
                <Input
                  className="max-w-xs"
                  value={form?.title}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    _updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['title'])}
                />
                {!!getValidationError(validationErrors, ['title']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['title'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Description</Label>
                <Description>A brief description of what this style template is for</Description>
                <Textarea
                  value={form?.description}
                  rows={2}
                  resizable={false}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    _updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['description'])}
                />
                {!!getValidationError(validationErrors, ['description']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Start Pages</Label>
                <Description>
                  Upload any PDF to statically populate the first pages of documents generated using
                  this style. This can include cover pages, disclosures, or any other static
                  information.
                </Description>
                {!!props.initialTemplate?.prefixFiles?.length && !resetPrefixFiles && (
                  <div data-slot="control" className="flex flex-row gap-4">
                    {props.initialTemplate.prefixFiles.map((file) => (
                      <div key={file.id} className="flex flex-row gap-2">
                        <DocumentIcon className="size-10 text-neutral-400" />
                        <div className="flex flex-col">
                          <div className="text-sm text-neutral-700 truncate text-ellipsis">
                            {file.name}
                          </div>
                          <a
                            onClick={() => {
                              setPrefixFiles([]);
                              setResetPrefixFiles(true);
                            }}
                            className="text-sm text-primary-500 hover:text-primary-900 hover:underline"
                          >
                            Remove File
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(!props.initialTemplate?.prefixFiles?.length || resetPrefixFiles) && (
                  <FileInput multiple onChange={(files) => setPrefixFiles(files)} />
                )}
              </Field>
              <Field>
                <Label>End Pages</Label>
                <Description>
                  Upload any PDF to statically populate the last pages of documents generated using
                  this style. This can include cover pages, disclosures, or any other static
                  information.
                </Description>
                {!!props.initialTemplate?.suffixFiles?.length && !resetSuffixFiles && (
                  <div data-slot="control" className="flex flex-row gap-4">
                    {props.initialTemplate.suffixFiles.map((file) => (
                      <div key={file.id} className="flex flex-row gap-2">
                        <DocumentIcon className="size-10 text-neutral-400" />
                        <div className="flex flex-col">
                          <div className="text-sm text-neutral-700 truncate text-ellipsis">
                            {file.name}
                          </div>
                          <a
                            onClick={() => {
                              setSuffixFiles([]);
                              setResetSuffixFiles(true);
                            }}
                            className="text-sm text-primary-500 hover:text-primary-900 hover:underline"
                          >
                            Remove File
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(!props.initialTemplate?.suffixFiles?.length || resetSuffixFiles) && (
                  <FileInput multiple onChange={(files) => setSuffixFiles(files)} />
                )}
              </Field>
              <Field>
                <Label>Base Style</Label>
                <Description>
                  If you'd like, choose a predesigned base style as a starting point for this style
                  template.
                </Description>
                <Select
                  value={form?.baseStyleId || ''}
                  onChange={(e) => {
                    const baseStyleId = e.currentTarget.value;
                    updateForm(['baseStyleId'], (prev) => ({
                      ...prev,
                      baseStyleId: baseStyleId || null
                    }));
                  }}
                >
                  <option value="">None</option>
                  <option value="builtin:classic-professional">Classic Professional</option>
                  <option value="builtin:modern-minimalist">Modern Minimalist</option>
                  <option value="builtin:warm-human">Warm Human</option>
                  <option value="builtin:elegant-premium">Elegant Premium</option>
                  <option value="builtin:technical-structured">Technical and Structured</option>
                </Select>
              </Field>
            </FieldGroup>
          </Fieldset>
          <div className="mt-8 flex flex-col items-center gap-3 *:w-full sm:flex-row sm:*:w-auto">
            <Button
              type="button"
              color="white"
              disabled={navigation.state === 'submitting'}
              onClick={() => navigate(-1)}
            >
              Close
            </Button>
            <Button
              type="submit"
              color="sky"
              disabled={!form}
              loading={navigation.state === 'submitting'}
            >
              {props.editMode ? 'Update Style' : 'Create Style'}
            </Button>
          </div>
        </Form>
        <div className="col-span-1 sm:col-span-2">
          {renderPreview && (
            <iframe
              src={`${getStaticsBaseUrl()}/agency/preview-html/${props.initialTemplate?.id}?v=${renderPreviewVersion}`}
              className="h-[calc(100vh-6rem)] w-full sticky top-0 rounded-lg bg-neutral-50 border border-neutral-100"
            />
          )}
          {renderingPreview && (
            <div className="text-center w-full px-16 py-32 rounded-lg bg-neutral-50 border border-neutral-100 sticky top-0">
              <Spinner size="size-12" color="neutral" />
              <h3 className="mt-2 text-sm font-semibold text-neutral-700">Generating preview...</h3>
              <p className="mt-1 text-sm text-neutral-500">This may take a few seconds.</p>
            </div>
          )}
          {!renderPreview && !renderingPreview && (
            <div className="text-center w-full px-16 py-32 rounded-lg bg-neutral-50 border border-neutral-100 sticky top-0">
              <EyeSlashIcon className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-semibold text-neutral-700">Preview not available</h3>
              <p className="mt-1 text-sm text-neutral-500">
                Save this style template to generate a preview.
              </p>
            </div>
          )}
        </div>
      </div>
    </MountableSlideout>
  );
};
